import moment from "moment";

import CONSTANTS from "../const/constants";

// WhatsApp's customer service window stays open for 24 hours after the
// customer's last message.
const CUSTOMER_SERVICE_WINDOW_MS = 24 * 60 * 60 * 1000;

// Epoch milliseconds of a Date, an ISO string or epoch milliseconds; null
// when the value is missing or is not a date.
const toTimestamp = (date) => {
  const timestamp = date ? new Date(date).getTime() : NaN;
  return Number.isNaN(timestamp) ? null : timestamp;
};

// Latest of the given dates as epoch milliseconds, or null when none is valid.
export const latestTimestamp = (...dates) =>
  dates
    .map(toTimestamp)
    .reduce(
      (latest, timestamp) =>
        timestamp !== null && (latest === null || timestamp > latest) ? timestamp : latest,
      null
    );

export const getConversationPlatformName = (conversation) =>
  conversation?.platform?.name || conversation?.channel?.platform?.name;

// The customer-message rule is WhatsApp's. Any other platform keeps the
// `windowExpiration` as its only source (Messenger has its own 7-day check in
// MessagesSection).
export const usesCustomerMessageWindow = (platformName) =>
  platformName === CONSTANTS.WHATSAPP_PLATFORM;

// Timestamp at which the window ends, or null when there is nothing to derive
// it from. A `windowExpiration`, stored or carried by a status event, is not
// reliable on its own: inbound messages often lack it and outgoing ones are
// frequently stamped with their send time, so on WhatsApp it only counts when
// it is later than the customer's last message plus 24 hours.
const getWindowEndTimestamp = ({ lastClientMessageAt, expiration, platformName }) => {
  const expirationTimestamp = toTimestamp(expiration);
  const lastClientMessageTimestamp = toTimestamp(lastClientMessageAt);
  if (!usesCustomerMessageWindow(platformName) || lastClientMessageTimestamp === null) {
    return expirationTimestamp;
  }

  return latestTimestamp(
    lastClientMessageTimestamp + CUSTOMER_SERVICE_WINDOW_MS,
    expirationTimestamp
  );
};

/**
 * Whole minutes left in the messaging window of a conversation, the value the
 * "Ventana de conversación activa/inactiva" banner reads: `>= 1` is an open
 * window, a negative value a closed one and `null` means there is nothing to
 * show. Every writer of the banner goes through this rule.
 *
 * Dates are a Date, an ISO string or epoch milliseconds.
 *
 * @param {object} input
 * @param {*} input.lastClientMessageAt When the customer last wrote, if known.
 * @param {*} input.expiration Stored or event-carried `windowExpiration`.
 * @param {string} input.platformName Platform of the conversation.
 * @param {number} input.now Current time in epoch milliseconds.
 * @returns {number|null}
 */
export const getWindowRemainingMinutes = ({ lastClientMessageAt, expiration, platformName, now }) => {
  const windowEnd = getWindowEndTimestamp({ lastClientMessageAt, expiration, platformName });
  if (windowEnd === null) return null;

  return Math.floor(moment.duration(windowEnd - now, "milliseconds").asMinutes());
};
